/**
 * CLIKKER — Public Creator Storefront
 * Asset: clikker-store.js
 * Version: 9.1.3.2 — updated default creator banner
 *
 * Requires:
 * - Firebase compat SDK
 * - clikker-firebase.js
 * - clikker-product-engine.js
 * - clikker-product-workflows.js
 *
 * Checkout:
 * - Product modal + buyer requirements + order/payment flow live in clikker-store-checkout.js
 *
 * Page:
 * - page.clikker-store.liquid
 *
 * Header:
 * - Uses the normal Shopify / Clikker portal theme header
 * - Store-specific share action remains inside the creator hero
 */
(function () {
  "use strict";

  var VERSION = "9.1.3.2";
  var CORE_TIMEOUT_MS = 15000;
  var QUERY_TIMEOUT_MS = 15000;

  /*
   * GLOBAL DEFAULT CREATOR BANNER
   * ----------------------------------------------------------------------
   * Used only when a creator has not uploaded a custom store banner.
   * Creator-uploaded banner always has priority.
   */
  var DEFAULT_CREATOR_BANNER_URL =
    clean(window.CLIKKER_DEFAULT_CREATOR_BANNER) ||
    "https://cdn.shopify.com/s/files/1/0928/6136/6587/files/Creator_Banner.jpg?v=1786798200";

  var root = document.getElementById("clikkerPublicStoreRoot");
  var StoreAPI = window.ClikkerStore = window.ClikkerStore || {};

  if (!root) {
    console.error("[Clikker Storefront] Required storefront root is missing.");
    return;
  }

  var ProductEngine = window.ClikkerProductEngine || null;

  function workflowMeta(type) {
    if (!ProductEngine || typeof ProductEngine.getWorkflowMeta !== "function") {
      return null;
    }
    return ProductEngine.getWorkflowMeta(type);
  }

  function workflowDefinition(type) {
    if (!ProductEngine || typeof ProductEngine.getWorkflow !== "function") {
      return null;
    }
    return ProductEngine.getWorkflow(type);
  }

  function normalizedProductType(value) {
    var type = clean(value).toLowerCase();
    if (ProductEngine && typeof ProductEngine.normalizeType === "function") {
      return ProductEngine.normalizeType(type || "custom_service");
    }
    return type || "custom_service";
  }

  var pageURL = new URL(window.location.href);
  var slug = String(
    pageURL.searchParams.get("creator") ||
    pageURL.searchParams.get("store") ||
    ""
  ).trim().toLowerCase();
  slug = normalizeSlug(slug);
  var previewMode = pageURL.searchParams.get("preview") === "1";

  var currentStore = null;
  var currentCreator = null;
  var allProducts = [];
  var currentFilter = "all";
  var sectionObserver = null;

  var PRODUCT_ICONS = {
    creator_reply: "💬",
    video_message: "🎥",
    scheduled_call: "📞",
    digital_download: "📘",
    custom_service: "✨",
    social_promotion: "📱",
    content_access: "🔒",
    community_access: "👥"
  };

  var PRODUCT_LABELS = {
    creator_reply: "Personal Reply",
    video_message: "Video Message",
    scheduled_call: "One-to-One Call",
    digital_download: "Digital Product",
    custom_service: "Custom Service",
    social_promotion: "Social Promotion",
    content_access: "Exclusive Content",
    community_access: "Paid Community"
  };

  var PRODUCT_CTAS = {
    creator_reply: "Get a Reply",
    video_message: "Request Video",
    scheduled_call: "Book a Call",
    digital_download: "Get Access",
    custom_service: "Request Service",
    social_promotion: "Start Collaboration",
    content_access: "Unlock Access",
    community_access: "Join Community"
  };

  var CATEGORY_PRESENTATION = {
    influencer: {
      label: "Influencer",
      tagline: "Connect, collaborate and book exclusive creator experiences.",
      about: "creates direct experiences and collaboration opportunities for their audience through Clikker.",
      visual: "social"
    },
    creator: {
      label: "Creator",
      tagline: "Connect directly through personalised creator experiences.",
      about: "offers personalised experiences, digital products and direct ways to connect through Clikker.",
      visual: "creator"
    },
    coach: {
      label: "Coach",
      tagline: "Book focused sessions, guidance and personalised support.",
      about: "offers focused guidance and personalised sessions designed to help people move forward with clarity.",
      visual: "coach"
    },
    educator: {
      label: "Educator",
      tagline: "Learn directly through practical sessions and creator-led resources.",
      about: "shares practical knowledge, learning experiences and useful resources directly through Clikker.",
      visual: "education"
    },
    artist: {
      label: "Artist",
      tagline: "Discover original creative work and personalised experiences.",
      about: "shares original creative work, personalised commissions and direct audience experiences through Clikker.",
      visual: "artist"
    },
    musician: {
      label: "Musician",
      tagline: "Connect through music, personalised moments and exclusive experiences.",
      about: "offers music-led experiences, personalised moments and exclusive ways for fans to connect.",
      visual: "music"
    },
    fitness: {
      label: "Fitness Creator",
      tagline: "Book personalised fitness, wellness and motivation experiences.",
      about: "shares fitness and wellness experiences designed to help their audience stay motivated and engaged.",
      visual: "fitness"
    },
    gamer: {
      label: "Gaming Creator",
      tagline: "Unlock personalised gaming experiences and direct creator access.",
      about: "offers gaming-focused experiences, personalised interactions and exclusive ways to connect.",
      visual: "gaming"
    },
    fashion: {
      label: "Fashion Creator",
      tagline: "Explore style, collaborations and personalised creator experiences.",
      about: "shares style-led experiences, collaborations and personalised ways for their audience to connect.",
      visual: "fashion"
    }
  };

  var PRODUCT_PRESENTATION = {
    creator_reply: {
      eyebrow: "Direct message",
      fallbackDescription: "Send your question or message and receive a personalised reply directly from the creator.",
      accent: "message"
    },
    video_message: {
      eyebrow: "Personal video",
      fallbackDescription: "Request a personalised video created especially for you or someone you care about.",
      accent: "video"
    },
    scheduled_call: {
      eyebrow: "Private session",
      fallbackDescription: "Book dedicated one-to-one time with the creator for a focused conversation.",
      accent: "call"
    },
    digital_download: {
      eyebrow: "Instant access",
      fallbackDescription: "Get access to a creator-made digital product immediately after secure payment.",
      accent: "digital"
    },
    custom_service: {
      eyebrow: "Made for you",
      fallbackDescription: "Request a personalised creator service tailored to your requirements.",
      accent: "service"
    },
    social_promotion: {
      eyebrow: "Brand collaboration",
      fallbackDescription: "Collaborate with the creator on a social media promotion, campaign or brand activation.",
      accent: "social"
    },
    content_access: {
      eyebrow: "Exclusive access",
      fallbackDescription: "Unlock creator-only content and access it securely after purchase.",
      accent: "digital"
    },
    community_access: {
      eyebrow: "Community access",
      fallbackDescription: "Join the creator's paid community and access the benefits included with this offer.",
      accent: "social"
    }
  };

  function categoryPresentation(category) {
    var key = clean(category).toLowerCase().replace(/[^a-z0-9]+/g, "_");

    if (CATEGORY_PRESENTATION[key]) {
      return CATEGORY_PRESENTATION[key];
    }

    if (/influenc|social|content/.test(key)) return CATEGORY_PRESENTATION.influencer;
    if (/coach|mentor|consult/.test(key)) return CATEGORY_PRESENTATION.coach;
    if (/teach|educat|trainer/.test(key)) return CATEGORY_PRESENTATION.educator;
    if (/artist|design|illustrat|creative/.test(key)) return CATEGORY_PRESENTATION.artist;
    if (/music|singer|rapper|dj/.test(key)) return CATEGORY_PRESENTATION.musician;
    if (/fitness|health|wellness|yoga/.test(key)) return CATEGORY_PRESENTATION.fitness;
    if (/game|stream|esport/.test(key)) return CATEGORY_PRESENTATION.gamer;
    if (/fashion|style|beauty|model/.test(key)) return CATEGORY_PRESENTATION.fashion;

    return CATEGORY_PRESENTATION.creator;
  }

  function offerSummary(types) {
    var labels = (types || []).map(typeLabel);

    if (!labels.length) return "personalised creator experiences";
    if (labels.length === 1) return labels[0].toLowerCase();
    if (labels.length === 2) return labels[0].toLowerCase() + " and " + labels[1].toLowerCase();

    return "creator services, digital products and personalised experiences";
  }

  function defaultTagline(creatorName, category, types) {
    var presentation = categoryPresentation(category);
    var offers = offerSummary(types);

    if (types && types.length) {
      return "Connect with " + creatorName + " through " + offers + ".";
    }

    return presentation.tagline;
  }

  function defaultAbout(creatorName, category, types) {
    var presentation = categoryPresentation(category);
    var offerText = types && types.length
      ? " Current offers include " + offerSummary(types) + "."
      : "";

    return creatorName + " " + presentation.about + offerText;
  }

  function looksLikeWeakProfileText(value) {
    var text = clean(value);
    if (!text) return true;

    var normalized = text.toLowerCase().replace(/\s+/g, " ").trim();

    if (normalized.length < 38) return true;

    var weakExact = [
      "creator information will appear here",
      "welcome to my creator store",
      "welcome to creator store",
      "collaboration product and",
      "about me",
      "bio"
    ];

    if (weakExact.indexOf(normalized) >= 0) return true;
    if (/\b(lorem ipsum|test|testing|sample text|coming soon)\b/i.test(normalized)) return true;
    if (/\b(and|or|with|for|to|the|a|an)\s*$/i.test(normalized)) return true;

    return false;
  }

  function resolvedAboutText(rawAbout, creatorName, category, types) {
    return looksLikeWeakProfileText(rawAbout)
      ? defaultAbout(creatorName, category, types)
      : clean(rawAbout);
  }

  function resolvedTaglineText(rawTagline, creatorName, category, types) {
    var tagline = clean(rawTagline);

    if (!tagline || tagline.length < 24 || /welcome to .*creator store/i.test(tagline)) {
      return defaultTagline(creatorName, category, types);
    }

    return tagline;
  }

  function featuredOfferHeading(creatorName, product) {
    if (!product) return "Featured offer from " + creatorName;

    var type = typeOf(product);

    if (type === "social_promotion") return "Collaborate with " + creatorName;
    if (type === "scheduled_call") return "Book time with " + creatorName;
    if (type === "video_message") return "Get a personalised video from " + creatorName;
    if (type === "creator_reply") return "Get a personal reply from " + creatorName;
    if (type === "digital_download") return "Featured digital product";

    return typeLabel(type) + " from " + creatorName;
  }

  function countMeaningfulSocialLinks(store) {
    var socials = socialLinks(store);
    var keys = [
      "instagram",
      "youtube",
      "linkedin",
      "linkedIn",
      "website",
      "web",
      "facebook",
      "twitter",
      "x",
      "tiktok"
    ];

    var seen = {};
    var count = 0;

    keys.forEach(function (key) {
      var value = clean(socials && socials[key]);
      if (!value) return;

      var normalized = value.toLowerCase();
      if (seen[normalized]) return;

      seen[normalized] = true;
      count += 1;
    });

    return count;
  }

  function evaluateStoreMode(options) {
    options = options || {};

    var bannerImage = clean(options.bannerImage);
    var profileImage = clean(options.profileImage);
    var rawAbout = clean(options.rawAbout);
    var rawTagline = clean(options.rawTagline);
    var category = clean(options.category);
    var products = Array.isArray(options.products) ? options.products : [];
    var socialCount = Number(options.socialCount || 0);

    var meaningfulAbout = !looksLikeWeakProfileText(rawAbout);
    var meaningfulTagline =
      rawTagline.length >= 24 &&
      !/welcome to .*creator store/i.test(rawTagline);

    var score = 0;

    if (bannerImage) score += 2;
    if (profileImage) score += 2;
    if (meaningfulAbout) score += 2;
    if (meaningfulTagline) score += 1;
    if (socialCount >= 2) score += 2;
    else if (socialCount === 1) score += 1;
    if (products.length >= 2) score += 1;
    if (products.length >= 4) score += 1;
    if (category && category.toLowerCase() !== "creator") score += 1;

    var mode = "starter";

    if (score >= 7) {
      mode = "rich";
    } else if (score >= 4) {
      mode = "standard";
    }

    /*
      Hard guards:
      - A creator with both a real banner and avatar should never be forced into
        Starter mode merely because their bio is short.
      - A creator with no banner, no avatar and weak profile text is always Starter.
    */
    if (bannerImage && profileImage && mode === "starter") {
      mode = "standard";
    }

    if (
      bannerImage &&
      profileImage &&
      (meaningfulAbout || meaningfulTagline || socialCount >= 2 || products.length >= 2)
    ) {
      mode = "rich";
    }

    if (
      !bannerImage &&
      !profileImage &&
      !meaningfulAbout &&
      !meaningfulTagline &&
      socialCount === 0
    ) {
      mode = "starter";
    }

    return {
      mode: mode,
      score: score,
      meaningfulAbout: meaningfulAbout,
      meaningfulTagline: meaningfulTagline,
      socialCount: socialCount,
      productCount: products.length,
      hasBanner: !!bannerImage,
      hasAvatar: !!profileImage
    };
  }

  function generatedHeroVisual(category) {
    var visual = categoryPresentation(category).visual || "creator";
    var fallbackBanner = defaultCreatorBanner();

    return [
      '<div class="cps-generated-hero cps-generated-hero--' + esc(visual) + '"' +
        ' data-default-banner="true" aria-hidden="true">',
        fallbackBanner
          ? '<img class="cps-default-banner__image" src="' + esc(fallbackBanner) + '" alt="" loading="eager" decoding="async" fetchpriority="high">'
          : '',
      '</div>'
    ].join("");
  }

  function generatedProductVisual(type) {
    type = normalizedProductType(type);
    var presentation = PRODUCT_PRESENTATION[type] || PRODUCT_PRESENTATION.custom_service;
    var icon = PRODUCT_ICONS[type] || "✨";

    return [
      '<div class="cps-generated-product cps-generated-product--' + esc(presentation.accent) + '" aria-hidden="true">',
        '<div class="cps-generated-product__glow"></div>',
        '<div class="cps-generated-product__card">',
          '<span class="cps-generated-product__icon">' + esc(icon) + '</span>',
          '<span class="cps-generated-product__eyebrow">' + esc(presentation.eyebrow) + '</span>',
          '<span class="cps-generated-product__bar"></span>',
          '<span class="cps-generated-product__bar cps-generated-product__bar--short"></span>',
        '</div>',
      '</div>'
    ].join("");
  }

  function productFallbackDescription(product) {
    var type = typeOf(product);
    var presentation = PRODUCT_PRESENTATION[type] || PRODUCT_PRESENTATION.custom_service;
    return presentation.fallbackDescription;
  }

  function clean(value) {
    return String(value == null ? "" : value).trim();
  }

  function validColor(value, fallback) {
    var color = clean(value);
    return /^#[0-9a-f]{6}$/i.test(color) ? color : fallback;
  }

  function normalizeExternalURL(value, kind) {
    var text = clean(value);
    if (!text) return "";

    if (kind === "whatsapp") {
      if (/^https?:\/\//i.test(text)) return text;
      var digits = text.replace(/\D+/g, "");
      return digits ? "https://wa.me/" + digits : "";
    }

    if (/^(mailto:|https?:\/\/)/i.test(text)) return text;
    if (/^[\w.-]+\.[a-z]{2,}(?:\/.*)?$/i.test(text)) {
      return "https://" + text;
    }

    return text;
  }

  function normalizeSlug(value) {
    return clean(value)
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 80);
  }

  function toArray(value) {
    return Array.isArray(value) ? value : [];
  }

  function firstValue() {
    for (var i = 0; i < arguments.length; i += 1) {
      var value = arguments[i];
      if (value !== undefined && value !== null && value !== "") return value;
    }
    return "";
  }

  function nested(object, path, fallback) {
    try {
      var value = path.split(".").reduce(function (current, key) {
        return current && current[key] !== undefined ? current[key] : undefined;
      }, object);
      return value === undefined || value === null ? fallback : value;
    } catch (_) {
      return fallback;
    }
  }

  function boolValue(value, fallback) {
    if (value === true || value === false) return value;
    if (value === "true") return true;
    if (value === "false") return false;
    return fallback;
  }

  function withTimeout(promise, timeoutMs, message) {
    var timer = null;

    var timeoutPromise = new Promise(function (_, reject) {
      timer = setTimeout(function () {
        reject(new Error(message || "The request took too long."));
      }, timeoutMs);
    });

    return Promise.race([promise, timeoutPromise]).finally(function () {
      if (timer) clearTimeout(timer);
    });
  }

  function db() {
    if (window.Clikker && window.Clikker.db) {
      return window.Clikker.db;
    }

    if (window.firebase && typeof window.firebase.firestore === "function") {
      return window.firebase.firestore();
    }

    throw new Error("Clikker Firestore is unavailable.");
  }

  function serverTimestamp() {
    if (
      window.Clikker &&
      typeof window.Clikker.serverTimestamp === "function"
    ) {
      return window.Clikker.serverTimestamp();
    }

    if (
      window.firebase &&
      window.firebase.firestore &&
      window.firebase.firestore.FieldValue
    ) {
      return window.firebase.firestore.FieldValue.serverTimestamp();
    }

    return new Date();
  }

  async function waitForCore() {
    var started = Date.now();

    while (Date.now() - started < CORE_TIMEOUT_MS) {
      if (window.Clikker) {
        if (typeof window.Clikker.waitUntilReady === "function") {
          await withTimeout(
            window.Clikker.waitUntilReady(),
            CORE_TIMEOUT_MS,
            "Clikker could not finish loading."
          );
          return;
        }

        if (window.Clikker.readyPromise) {
          await withTimeout(
            window.Clikker.readyPromise,
            CORE_TIMEOUT_MS,
            "Clikker could not finish loading."
          );
          return;
        }

        if (window.Clikker.ready === true && window.Clikker.db) {
          return;
        }
      }

      await new Promise(function (resolve) {
        setTimeout(resolve, 50);
      });
    }

    throw new Error("Clikker core did not load.");
  }

  function esc(value) {
    if (window.Clikker && typeof window.Clikker.escapeHTML === "function") {
      return window.Clikker.escapeHTML(value == null ? "" : String(value));
    }

    return String(value == null ? "" : value).replace(/[&<>"']/g, function (character) {
      return {
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#039;"
      }[character];
    });
  }

  function safeUrl(value, kind) {
    var text = normalizeExternalURL(value, kind);
    if (!text) return "";

    if (window.Clikker && typeof window.Clikker.safeURL === "function") {
      return window.Clikker.safeURL(text, {
        allowRelative: false,
        allowedProtocols: ["https:", "http:", "mailto:"]
      });
    }

    try {
      var url = new URL(text, window.location.origin);
      return ["https:", "http:", "mailto:"].indexOf(url.protocol) >= 0 ? url.href : "";
    } catch (_) {
      return "";
    }
  }

  function money(value, currency) {
    if (window.Clikker && typeof window.Clikker.formatCurrency === "function") {
      return window.Clikker.formatCurrency(Number(value || 0), {
        currency: currency || "INR",
        maximumFractionDigits: 0
      });
    }

    try {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: currency || "INR",
        maximumFractionDigits: 0
      }).format(Number(value || 0));
    } catch (_) {
      return "₹" + Number(value || 0).toLocaleString("en-IN");
    }
  }

  function typeOf(product) {
    return normalizedProductType(firstValue(
      product.workflowType,
      product.workflow_type,
      product.productType,
      product.product_type,
      product.fulfilmentType,
      product.fulfillmentType,
      product.type,
      "custom_service"
    ));
  }

  function typeLabel(type) {
    type = normalizedProductType(type);
    var meta = workflowMeta(type);
    return clean(
      firstValue(
        meta && meta.label,
        PRODUCT_LABELS[type],
        clean(type).replace(/_/g, " ")
      )
    );
  }

  function productCTA(type) {
    type = normalizedProductType(type);
    return PRODUCT_CTAS[type] || "View Offer";
  }

  function productIcon(product) {
    var type = typeOf(product);
    var meta = workflowMeta(type);
    return clean(firstValue(meta && meta.icon, PRODUCT_ICONS[type], "✨"));
  }

  /*
   * PRODUCT COVER RESOLUTION
   * ----------------------------------------------------------------------
   * Display priority:
   * 1. Creator-uploaded product cover
   * 2. Workflow default cover from clikker-product-workflows.js v1.2.3+
   * 3. Existing generated Clikker product visual
   *
   * Workflow default images are display fallbacks only and are not written
   * into each product document.
   */
  function workflowDefaultCover(type) {
    type = normalizedProductType(type || "custom_service");

    var workflow = workflowDefinition(type);
    var meta = workflow && workflow.meta ? workflow.meta : null;
    var defaults = workflow && workflow.defaults ? workflow.defaults : null;

    var url = clean(firstValue(
      meta && meta.defaultCoverImage,
      defaults && defaults.defaultCoverImage,
      ""
    ));

    if (
      !url &&
      window.ClikkerProductWorkflows &&
      typeof window.ClikkerProductWorkflows.getDefaultCoverImage === "function"
    ) {
      url = clean(window.ClikkerProductWorkflows.getDefaultCoverImage(type));
    }

    return safeImageUrl(url);
  }

  function imageFromValue(value) {
    if (!value) return "";

    if (typeof value === "string") {
      return safeImageUrl(value);
    }

    if (typeof value === "object") {
      return safeImageUrl(firstValue(
        value.downloadURL,
        value.downloadUrl,
        value.url,
        value.src,
        value.secureURL,
        value.secure_url,
        ""
      ));
    }

    return "";
  }

  function creatorProductCover(product) {
    product = product || {};
    var raw = rawOf(product);

    var directCandidates = [
      product.coverImage,
      product.cover_image,
      product.coverImageURL,
      product.coverImageUrl,
      product.cover_image_url,
      product.thumbnail,
      product.thumbnailURL,
      product.thumbnailUrl,
      product.thumbnail_url,
      product.image,
      product.imageURL,
      product.imageUrl,
      product.image_url,
      raw.coverImage,
      raw.cover_image,
      raw.coverImageURL,
      raw.coverImageUrl,
      raw.cover_image_url,
      raw.thumbnail,
      raw.thumbnailURL,
      raw.thumbnailUrl,
      raw.thumbnail_url
    ];

    for (var i = 0; i < directCandidates.length; i += 1) {
      var resolved = imageFromValue(directCandidates[i]);
      if (resolved) return resolved;
    }

    return "";
  }

  function productCover(product) {
    return creatorProductCover(product) || workflowDefaultCover(typeOf(product));
  }

  function productCoverSource(product) {
    if (creatorProductCover(product)) return "creator";
    if (workflowDefaultCover(typeOf(product))) return "workflow";
    return "generated";
  }

  function rawOf(value) {
    return value && value.raw ? value.raw : {};
  }

  function fulfilmentOf(product) {
    return firstValue(
      product.fulfilment,
      product.fulfillment,
      nested(product, "raw.fulfilment", null),
      nested(product, "raw.fulfillment", null),
      {}
    ) || {};
  }

  function toSnakeCase(value) {
    return clean(value)
      .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
      .replace(/[\s-]+/g, "_")
      .replace(/[^a-zA-Z0-9_]/g, "")
      .toLowerCase();
  }

  function toCamelCase(value) {
    return toSnakeCase(value).replace(/_([a-z0-9])/g, function (_, character) {
      return character.toUpperCase();
    });
  }

  function productValue(product, key, fallback) {
    product = product || {};
    var fulfilment = fulfilmentOf(product);
    var snakeKey = toSnakeCase(key);
    var camelKey = toCamelCase(key);
    var raw = rawOf(product);
    var rawFulfilment = firstValue(raw.fulfilment, raw.fulfillment, {}) || {};

    return firstValue(
      product[key],
      product[snakeKey],
      product[camelKey],
      fulfilment[key],
      fulfilment[snakeKey],
      fulfilment[camelKey],
      raw[key],
      raw[snakeKey],
      raw[camelKey],
      rawFulfilment[key],
      rawFulfilment[snakeKey],
      rawFulfilment[camelKey],
      fallback
    );
  }

  function humanizeValue(value, fallback) {
    var text = clean(value || fallback || "");
    if (!text) return "";

    return text
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .replace(/\b\w/g, function (character) {
        return character.toUpperCase();
      });
  }

  function safeImageUrl(value) {
    var text = clean(value);
    if (!text) return "";

    if (window.Clikker && typeof window.Clikker.safeURL === "function") {
      return window.Clikker.safeURL(text, {
        allowRelative: false,
        allowedProtocols: ["https:", "http:"]
      });
    }

    try {
      var parsed = new URL(text, window.location.origin);
      return ["https:", "http:"].indexOf(parsed.protocol) >= 0 ? parsed.href : "";
    } catch (_) {
      return "";
    }
  }


  function defaultCreatorBanner() {
    return safeImageUrl(DEFAULT_CREATOR_BANNER_URL);
  }

  function errorView(title, text) {
    root.innerHTML = [
      '<div class="cps-error">',
        '<section class="cps-error__card">',
          '<div class="cps-brand__mark cps-error__mark">C</div>',
          '<h1>' + esc(title) + '</h1>',
          '<p>' + esc(text) + '</p>',
          '<div class="cps-error__actions">',
            '<a class="cps-btn cps-btn--primary" href="/">Return to Clikker</a>',
          '</div>',
        '</section>',
      '</div>'
    ].join("");
  }


  function activateStoreGateway() {
    document.title = "Find a Creator | Clikker";

    var form = document.getElementById("cpsCreatorFinder");
    var input = document.getElementById("cpsCreatorHandle");
    var error = document.getElementById("cpsCreatorFinderError");

    if (!form || !input || !error) {
      errorView(
        "Creator finder unavailable",
        "The creator finder could not load. Please refresh this page."
      );
      return;
    }

    function normalizedFinderHandle() {
      var value = clean(input.value)
        .replace(/^@+/, "")
        .toLowerCase();

      var handle = normalizeSlug(value);
      input.value = handle;
      return handle;
    }

    input.addEventListener("input", function () {
      input.value = clean(input.value).replace(/^@+/, "");
      error.textContent = "";
      input.removeAttribute("aria-invalid");
    });

    form.addEventListener("submit", function (event) {
      event.preventDefault();

      var handle = normalizedFinderHandle();

      if (!handle || handle.length < 3) {
        error.textContent = "Enter a valid creator username.";
        input.setAttribute("aria-invalid", "true");
        input.focus();
        return;
      }

      error.textContent = "";
      input.removeAttribute("aria-invalid");

      window.location.assign(
        "/pages/c?creator=" + encodeURIComponent(handle)
      );
    });

    window.setTimeout(function () {
      input.focus();
    }, 0);

    console.info("[Clikker Creator Finder] Ready", VERSION);
  }

  function showToast(message, type) {
    if (
      window.Clikker &&
      typeof window.Clikker.showToast === "function"
    ) {
      window.Clikker.showToast(message, type || "info");
      return;
    }

    var existing = document.getElementById("cpsLocalToast");
    if (existing) existing.remove();

    var toast = document.createElement("div");
    toast.id = "cpsLocalToast";
    toast.className = "cps-toast cps-toast--" + (type || "info");
    toast.setAttribute("role", type === "error" ? "alert" : "status");
    toast.textContent = message;

    document.body.appendChild(toast);

    setTimeout(function () {
      toast.remove();
    }, 2600);
  }

  function socialLinks(store) {
    return firstValue(
      store.socialLinks,
      nested(store, "raw.socialLinks", null),
      nested(store, "raw.social_links", null),
      {}
    ) || {};
  }

  function supportEmail(store, creator) {
    return clean(firstValue(
      store.supportEmail,
      nested(store, "contact.supportEmail", ""),
      nested(store, "raw.contact.supportEmail", ""),
      nested(store, "raw.supportEmail", ""),
      nested(store, "raw.support_email", ""),
      nested(store, "raw.contactEmail", ""),
      nested(store, "raw.contact_email", ""),
      creator && creator.supportEmail,
      nested(creator, "raw.supportEmail", ""),
      nested(creator, "raw.support_email", ""),
      nested(creator, "raw.email", "")
    ));
  }

  function socialLink(label, value) {
    var kind = clean(label).toLowerCase();
    var href = safeUrl(value, kind);
    var key = normalizeSlug(kind) || "social";

    return href
      ? '<a href="' + esc(href) + '" target="_blank" rel="noopener noreferrer" data-social="' + esc(key) + '" aria-label="Open ' + esc(label) + '">' + esc(label) + '</a>'
      : "";
  }

  function sectionEnabled(store, key, fallback) {
    var sections = firstValue(
      store.sections,
      nested(store, "raw.sections", null),
      nested(store, "raw.pageSections", null),
      nested(store, "raw.page_sections", null),
      {}
    ) || {};

    var value = firstValue(
      sections[key],
      sections["show" + key.charAt(0).toUpperCase() + key.slice(1)],
      nested(store, "raw.show" + key.charAt(0).toUpperCase() + key.slice(1), "")
    );

    return boolValue(value, fallback);
  }

  function brandingOf(store) {
    var branding = firstValue(
      store.branding,
      nested(store, "raw.branding", null),
      {}
    ) || {};

    var bannerPosition = clean(firstValue(
      branding.bannerPosition,
      branding.banner_position,
      nested(store, "raw.branding.bannerPosition", ""),
      nested(store, "raw.branding.banner_position", ""),
      "center"
    )).toLowerCase();

    if (["left", "center", "right"].indexOf(bannerPosition) === -1) {
      bannerPosition = "center";
    }

    return {
      primary: validColor(firstValue(
        branding.primaryColor,
        branding.primary_color,
        store.primaryColor,
        nested(store, "raw.primaryColor", ""),
        nested(store, "raw.primary_color", "")
      ), "#101828"),
      secondary: validColor(firstValue(
        branding.secondaryColor,
        branding.secondary_color,
        store.secondaryColor,
        nested(store, "raw.secondaryColor", ""),
        nested(store, "raw.secondary_color", "")
      ), "#667085"),
      button: validColor(firstValue(
        branding.buttonColor,
        branding.button_color,
        store.buttonColor,
        nested(store, "raw.buttonColor", ""),
        nested(store, "raw.button_color", "")
      ), "#101828"),
      text: validColor(firstValue(
        branding.textColor,
        branding.text_color,
        store.textColor,
        nested(store, "raw.textColor", ""),
        nested(store, "raw.text_color", "")
      ), "#101828"),
      bannerPosition: bannerPosition,
      showSocialLinks: boolValue(firstValue(
        branding.showSocialLinks,
        branding.show_social_links,
        nested(store, "raw.branding.showSocialLinks", ""),
        nested(store, "raw.branding.show_social_links", "")
      ), true)
    };
  }

  function deliveryMeta(product) {
    var type = typeOf(product);
    var items = [];

    if (type === "scheduled_call") {
      if (productValue(product, "duration_minutes", productValue(product, "duration", ""))) {
        items.push(productValue(product, "duration_minutes", productValue(product, "duration", "")) + " min");
      }
      if (productValue(product, "platform", "")) {
        items.push(productValue(product, "platform", ""));
      }
    } else if (type === "digital_download") {
      items.push("Instant access");
      if (productValue(product, "access_duration", productValue(product, "accessDuration", ""))) {
        items.push(productValue(product, "access_duration", productValue(product, "accessDuration", "")));
      }
    } else if (type === "community_access" || type === "content_access") {
      if (productValue(product, "access_duration", productValue(product, "accessDuration", ""))) {
        items.push(productValue(product, "access_duration", productValue(product, "accessDuration", "")));
      }
      if (String(productValue(product, "billing_type", productValue(product, "billingType", ""))).toLowerCase() === "monthly") {
        items.push("Monthly");
      }
    } else {
      if (Number(productValue(product, "delivery_hours", productValue(product, "deliveryHours", 0))) > 0) {
        items.push("Within " + Number(productValue(product, "delivery_hours", productValue(product, "deliveryHours", 0))) + " hrs");
      }
      if (Number(productValue(product, "daily_limit", productValue(product, "dailyLimit", 0))) > 0) {
        items.push(Number(productValue(product, "daily_limit", productValue(product, "dailyLimit", 0))) + "/day");
      }
    }

    return items.slice(0, 2);
  }

  function productCard(product) {
    var safeImage = productCover(product);
    var coverSource = productCoverSource(product);
    var title = clean(firstValue(product.title, product.name, "Untitled product"));
    var description = clean(firstValue(
      product.shortDescription,
      product.short_description,
      product.description,
      productFallbackDescription(product)
    ));
    var type = typeOf(product);
    var metas = deliveryMeta(product);
    var cta = productCTA(type);

    return [
      '<article class="cps-product" data-product-type="' + esc(type) + '" data-product-id="' + esc(product.id) + '" data-cover-source="' + esc(coverSource) + '">',
        '<div class="cps-product__media" data-cover-source="' + esc(coverSource) + '">',
          safeImage
            ? '<img src="' + esc(safeImage) + '" alt="' + esc(title) + '" loading="lazy" decoding="async">'
            : generatedProductVisual(type),
          previewMode
            ? '<span class="cps-product__status">' + esc(product.status || "draft") + '</span>'
            : "",
        '</div>',
        '<div class="cps-product__body">',
          '<span class="cps-product__type">' + esc(typeLabel(type)) + '</span>',
          '<h3>' + esc(title) + '</h3>',
          '<p class="cps-product__description">' + esc(description) + '</p>',
          metas.length
            ? '<div class="cps-product__meta">' + metas.map(function (meta) {
                return '<span>' + esc(meta) + '</span>';
              }).join("") + '</div>'
            : "",
          '<div class="cps-product__foot">',
            '<span class="cps-product__price">' + esc(money(product.price, product.currency || "INR")) + '</span>',
            '<button class="cps-btn cps-btn--primary" type="button" data-open-product="' + esc(product.id) + '" aria-label="' + esc(cta) + ' — ' + esc(title) + '">' + esc(cta) + '</button>',
          '</div>',
        '</div>',
      '</article>'
    ].join("");
  }

  function openProductFromCheckout(productId) {
    var Checkout = window.ClikkerStoreCheckout || null;

    if (Checkout && typeof Checkout.openProduct === "function") {
      Checkout.openProduct(productId);
      return;
    }

    console.warn(
      "[Clikker Storefront] Checkout module is not ready for product",
      productId
    );
    showToast("Checkout is still loading. Please try again.", "warning");
  }

  function bindProductButtons() {
    document.querySelectorAll("[data-open-product]").forEach(function (button) {
      button.onclick = function () {
        openProductFromCheckout(button.getAttribute("data-open-product"));
      };
    });
  }

  function renderProducts(products) {
    var grid = document.getElementById("cpsProductsGrid");
    if (!grid) return;

    grid.setAttribute("aria-busy", "true");

    grid.innerHTML = products.length
      ? products.map(productCard).join("")
      : '<div class="cps-empty cps-empty--full"><h3>No products found</h3><p>No products match this filter.</p></div>';

    bindProductButtons();
    bindImageFallbacks(currentStore && (currentStore.storeName || currentStore.name || "Creator"));
    grid.setAttribute("aria-busy", "false");
  }

  function bindFilters() {
    document.querySelectorAll("[data-product-filter]").forEach(function (button) {
      button.onclick = function () {
        var filter = button.getAttribute("data-product-filter") || "all";
        currentFilter = filter;

        document.querySelectorAll("[data-product-filter]").forEach(function (item) {
          var active = item === button;
          item.classList.toggle("is-active", active);
          item.setAttribute("aria-pressed", active ? "true" : "false");
        });

        renderProducts(
          filter === "all"
            ? allProducts
            : allProducts.filter(function (product) {
                return typeOf(product) === filter;
              })
        );
      };
    });
  }

  function normalizeList(store, keys) {
    for (var i = 0; i < keys.length; i += 1) {
      var value = nested(store, keys[i], null);
      if (Array.isArray(value)) return value;
    }
    return [];
  }

  function renderMiniCards(items, titleKey, textKey) {
    return items.map(function (item) {
      var title = clean(firstValue(
        typeof item === "string" ? item : item[titleKey],
        typeof item === "object" ? item.title : "",
        "Item"
      ));
      var text = clean(firstValue(
        typeof item === "object" ? item[textKey] : "",
        typeof item === "object" ? item.description : "",
        typeof item === "object" ? item.subtitle : "",
        ""
      ));

      return [
        '<article class="cps-mini-card">',
          '<h3>' + esc(title) + '</h3>',
          text ? '<p>' + esc(text) + '</p>' : "",
        '</article>'
      ].join("");
    }).join("");
  }

  function bindImageFallbacks(storeName) {
    document.querySelectorAll(".cps-avatar img").forEach(function (image) {
      image.onerror = function () {
        var avatar = image.parentElement;
        if (!avatar) return;
        avatar.innerHTML = esc(clean(storeName || "C").charAt(0).toUpperCase());
      };
    });

    document.querySelectorAll(".cps-default-banner__image").forEach(function (image) {
      image.onerror = function () {
        var generated = image.closest(".cps-generated-hero");
        image.onerror = null;
        image.remove();
        if (generated) {
          generated.classList.add("is-banner-image-failed");
        }
      };
    });

    document.querySelectorAll(".cps-hero__banner img").forEach(function (image) {
      image.onerror = function () {
        var hero = image.closest(".cps-hero");
        var banner = image.closest(".cps-hero__banner");
        if (!hero || !banner) return;

        image.onerror = null;
        banner.remove();

        hero.classList.remove("has-banner");
        hero.classList.add("no-banner");
        hero.setAttribute("data-banner-source", "default");

        if (!hero.querySelector(".cps-generated-hero")) {
          hero.insertAdjacentHTML(
            "afterbegin",
            generatedHeroVisual(
              currentStore && currentStore.category
                ? currentStore.category
                : "creator"
            )
          );
        }

        var rootStore = hero.closest(".cps");
        if (rootStore) {
          rootStore.setAttribute("data-has-banner", "false");
          rootStore.setAttribute("data-banner-source", "default");
        }
      };
    });

    document.querySelectorAll(".cps-product__media img").forEach(function (image) {
      image.onerror = function () {
        var media = image.parentElement;
        if (!media) return;

        var card = media.closest(".cps-product");
        var productType = card ? card.getAttribute("data-product-type") : "";

        image.onerror = null;
        image.remove();

        media.setAttribute("data-cover-source", "generated");
        if (card) card.setAttribute("data-cover-source", "generated");

        if (!media.querySelector(".cps-generated-product")) {
          media.insertAdjacentHTML(
            "beforeend",
            generatedProductVisual(productType)
          );
        }
      };
    });
  }

  async function shareCurrentStore() {
    var shareURL = window.location.origin + window.location.pathname +
      "?creator=" + encodeURIComponent(currentStore.slug || slug);
    var shareTitle = clean(firstValue(
      currentStore.storeName,
      currentStore.displayName,
      currentCreator && currentCreator.displayName,
      "Clikker Creator Store"
    ));

    try {
      if (navigator.share) {
        await navigator.share({
          title: shareTitle,
          text: "View " + shareTitle + " on Clikker",
          url: shareURL
        });
        return;
      }

      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareURL);
        showToast("Store link copied.", "success");
        return;
      }

      window.prompt("Copy this store link", shareURL);
    } catch (error) {
      if (error && error.name === "AbortError") return;
      console.warn("[Clikker Storefront] Share failed", error);
      window.prompt("Copy this store link", shareURL);
    }
  }

  function bindSectionNavigation() {
    if (sectionObserver) {
      sectionObserver.disconnect();
      sectionObserver = null;
    }

    var links = Array.prototype.slice.call(
      document.querySelectorAll(".cps-section-nav a[href^='#']")
    );

    if (!links.length) return;

    links.forEach(function (link) {
      link.onclick = function (event) {
        var target = document.querySelector(link.getAttribute("href"));
        if (!target) return;

        event.preventDefault();

        var portalHeader = document.querySelector(
          "header, .header-wrapper, .shopify-section-header-sticky, [data-header]"
        );
        var headerOffset = portalHeader
          ? Math.min(Math.max(portalHeader.getBoundingClientRect().height, 0), 220)
          : 0;

        var targetTop = target.getBoundingClientRect().top +
          window.pageYOffset -
          headerOffset -
          18;

        window.scrollTo({
          top: Math.max(0, targetTop),
          behavior: "smooth"
        });
      };
    });

    if (!("IntersectionObserver" in window)) return;

    sectionObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;

        links.forEach(function (link) {
          var active = link.getAttribute("href") === "#" + entry.target.id;
          link.classList.toggle("is-active", active);
          link.setAttribute("aria-current", active ? "true" : "false");
        });
      });
    }, {
      rootMargin: "-24% 0px -62% 0px",
      threshold: 0
    });

    links.forEach(function (link) {
      var target = document.querySelector(link.getAttribute("href"));
      if (target) sectionObserver.observe(target);
    });
  }

  function render(store, creator, products) {
    currentStore = store;
    currentCreator = creator;
    allProducts = products;

    var rawStore = rawOf(store);
    var socials = socialLinks(store);
    var email = supportEmail(store, creator);
    var branding = brandingOf(store);

    var profileImage = clean(firstValue(
      /* Store/custom image always has highest priority. */
      store.profileImage,
      store.profileImageURL,
      store.profileImageUrl,
      nested(store, "raw.profileImage", ""),
      nested(store, "raw.profileImageURL", ""),
      nested(store, "raw.profileImageUrl", ""),
      nested(store, "raw.profile_image", ""),

      /* Creator/custom image is next. */
      creator && creator.profileImage,
      creator && creator.profileImageURL,
      creator && creator.profileImageUrl,
      nested(creator, "raw.profileImage", ""),
      nested(creator, "raw.profileImageURL", ""),
      nested(creator, "raw.profileImageUrl", ""),
      nested(creator, "raw.profile_image", ""),

      /* Google/Firebase account image is fallback-only. */
      store.photoURL,
      store.googlePhotoURL,
      store.google_photo_url,
      nested(store, "raw.photoURL", ""),
      nested(store, "raw.photo_url", ""),
      nested(store, "raw.googlePhotoURL", ""),
      nested(store, "raw.google_photo_url", ""),
      creator && creator.photoURL,
      creator && creator.photo_url,
      creator && creator.googlePhotoURL,
      creator && creator.google_photo_url,
      nested(creator, "raw.photoURL", ""),
      nested(creator, "raw.photo_url", ""),
      nested(creator, "raw.googlePhotoURL", ""),
      nested(creator, "raw.google_photo_url", "")
    ));
    var bannerImage = clean(firstValue(
      store.bannerImage,
      store.bannerImageURL,
      store.bannerImageUrl,
      nested(store, "raw.bannerImage", ""),
      nested(store, "raw.bannerImageURL", ""),
      nested(store, "raw.bannerImageUrl", ""),
      nested(store, "raw.banner_image", ""),
      nested(store, "raw.banner_image_url", "")
    ));

    var safeBannerImage = safeImageUrl(bannerImage);
    var defaultBannerImage = defaultCreatorBanner();

    var creatorName = clean(firstValue(
      creator && creator.displayName,
      store.storeName,
      nested(creator, "raw.displayName", ""),
      nested(creator, "raw.name", ""),
      nested(creator, "raw.authDisplayName", ""),
      nested(creator, "raw.auth_display_name", ""),
      "Creator"
    ));
    var storeName = clean(firstValue(store.storeName, creatorName));
    var category = clean(firstValue(store.category, nested(store, "raw.category", ""), "Creator"));
    var creatorHandle = normalizeSlug(firstValue(
      store.slug,
      store.handle,
      rawStore.slug,
      rawStore.handle,
      slug
    ));

    var types = products.map(function (product) {
      return typeOf(product);
    }).filter(function (type, index, array) {
      return array.indexOf(type) === index;
    });

    var rawTagline = clean(firstValue(
      store.tagline,
      store.shortBio,
      nested(store, "raw.tagline", ""),
      nested(store, "raw.shortBio", ""),
      nested(store, "raw.short_bio", "")
    ));
    var tagline = resolvedTaglineText(rawTagline, creatorName, category, types);

    var rawAbout = clean(firstValue(
      store.about,
      store.shortBio,
      nested(store, "raw.about", ""),
      nested(store, "raw.shortBio", ""),
      creator && creator.bio,
      nested(creator, "raw.bio", "")
    ));
    var about = resolvedAboutText(rawAbout, creatorName, category, types);
    var audience = clean(firstValue(store.audience, nested(store, "raw.audience", ""), ""));

    var website = firstValue(
      socials.website,
      socials.web,
      nested(store, "raw.website", "")
    );
    var linkedin = firstValue(socials.linkedin, socials.linkedIn, "");

    var showHero = sectionEnabled(store, "hero", true);
    var showProducts = sectionEnabled(store, "products", true);
    var showAbout = sectionEnabled(store, "about", true);
    var showContact = sectionEnabled(store, "contact", true);
    var showAchievements = sectionEnabled(store, "achievements", true);
    var showBrands = sectionEnabled(store, "brands", true);
    var showTestimonials = sectionEnabled(store, "testimonials", true);
    var showFaq = sectionEnabled(store, "faq", true);

    var achievements = normalizeList(store, [
      "achievements",
      "raw.achievements",
      "credibility.achievements",
      "raw.credibility.achievements"
    ]);
    var brands = normalizeList(store, [
      "brands",
      "raw.brands",
      "credibility.brands",
      "raw.credibility.brands"
    ]);
    var testimonials = normalizeList(store, [
      "testimonials",
      "raw.testimonials",
      "credibility.testimonials",
      "raw.credibility.testimonials"
    ]);
    var faqs = normalizeList(store, [
      "faqs",
      "faq",
      "raw.faqs",
      "raw.faq"
    ]);

    var socialMarkup = branding.showSocialLinks
      ? [
          socialLink("Instagram", socials.instagram),
          socialLink("TikTok", socials.tiktok),
          socialLink("YouTube", socials.youtube),
          socialLink("X", firstValue(socials.x, socials.twitter, "")),
          socialLink("LinkedIn", linkedin),
          socialLink("Facebook", socials.facebook),
          socialLink("Website", website)
        ].join("")
      : "";

    var safeProfileImage = safeImageUrl(profileImage);
    var socialCount = countMeaningfulSocialLinks(store);
    var storeMode = evaluateStoreMode({
      bannerImage: safeBannerImage,
      profileImage: safeProfileImage,
      rawAbout: rawAbout,
      rawTagline: rawTagline,
      category: category,
      products: products,
      socialCount: socialCount
    });

    var heroPrimaryAction = "";
    if (showProducts && products.length === 1) {
      heroPrimaryAction =
        '<button class="cps-btn cps-btn--primary cps-btn--hero" type="button" data-open-product="' +
        esc(products[0].id) + '">' + esc(productCTA(typeOf(products[0]))) + '</button>';
    } else if (showProducts && products.length > 1) {
      heroPrimaryAction =
        '<a class="cps-btn cps-btn--primary cps-btn--hero" href="#products">Explore Offers</a>';
    }

    console.info("[Clikker Storefront] Banner", {
      source: safeBannerImage ? "creator" : (defaultBannerImage ? "default" : "generated"),
      hasCreatorBanner: Boolean(safeBannerImage),
      defaultBannerReady: Boolean(defaultBannerImage)
    });

    console.info("[Clikker Storefront] Creator identity", {
      creator: creatorName,
      hasAvatar: Boolean(safeProfileImage),
      avatarSource: safeProfileImage
        ? (
            clean(store.profileImage || store.profileImageURL || nested(store, "raw.profileImage", "") || nested(store, "raw.profileImageURL", ""))
              ? "store"
              : clean(
                  (creator && (creator.profileImage || creator.profileImageURL)) ||
                  nested(creator, "raw.profileImage", "") ||
                  nested(creator, "raw.profileImageURL", "")
                )
                ? "creator"
                : "google"
          )
        : "initial"
    });

    root.innerHTML = [
      '<div class="cps" data-preview="' + (previewMode ? "true" : "false") + '"' +
        ' data-store-mode="' + esc(storeMode.mode) + '"' +
        ' data-store-score="' + storeMode.score + '"' +
        ' data-starter-store="' + (storeMode.mode === "starter" ? "true" : "false") + '"' +
        ' data-standard-store="' + (storeMode.mode === "standard" ? "true" : "false") + '"' +
        ' data-rich-store="' + (storeMode.mode === "rich" ? "true" : "false") + '"' +
        ' data-has-banner="' + (safeBannerImage ? "true" : "false") + '"' +
        ' data-banner-source="' + (safeBannerImage ? "creator" : (defaultBannerImage ? "default" : "generated")) + '"' +
        ' data-has-avatar="' + (safeProfileImage ? "true" : "false") + '"' +
        ' data-product-count="' + products.length + '" data-design-generation="2">',

        '<div class="cps-preview" role="status">',
          '<div class="cps-preview__copy">',
            '<span class="cps-preview__badge">Preview mode</span>',
            '<div>',
              '<strong>You are viewing your private store preview.</strong>',
              '<small>Draft and paused products are visible only to you.</small>',
            '</div>',
          '</div>',
          '<a class="cps-btn cps-btn--secondary" href="/pages/clikker-profile?view=store">Return to Creator Dashboard</a>',
        '</div>',

        '<main class="cps-shell">',

          showHero
            ? [
                '<section class="cps-hero ' +
                  (safeBannerImage ? 'has-banner' : 'no-banner') +
                  (storeMode.mode === "starter" && !safeBannerImage ? ' is-starter-hero' : '') +
                  (storeMode.mode === "standard" ? ' is-standard-hero' : '') +
                  (storeMode.mode === "rich" ? ' is-rich-hero' : '') +
                  (safeProfileImage ? ' has-avatar' : ' no-avatar') +
                  '" data-store-mode="' + esc(storeMode.mode) + '"' +
                  ' data-banner-position="' + esc(branding.bannerPosition) + '">',
                  safeBannerImage
                    ? '<div class="cps-hero__banner" data-banner-source="creator"><img src="' + esc(safeBannerImage) + '" alt="" loading="eager" decoding="async" data-creator-banner="true"></div>'
                    : generatedHeroVisual(category),
                  '<div class="cps-hero__overlay"></div>',
                  '<div class="cps-hero__content">',
                    '<div class="cps-profile">',
                      '<div class="cps-avatar ' + (safeProfileImage ? 'has-image' : 'is-fallback') + '">',
                        safeProfileImage
                          ? '<img src="' + esc(safeProfileImage) + '" alt="' + esc(creatorName) + '" referrerpolicy="no-referrer">'
                          : '<span class="cps-avatar__initial">' + esc(storeName.charAt(0).toUpperCase()) + '</span>',
                      '</div>',
                      '<div>',
                        '<span class="cps-category">' + esc(category) + '</span>',
                        '<h1>' + esc(storeName) + '</h1>',
                        creatorHandle ? '<p class="cps-creator-handle">@' + esc(creatorHandle) + '</p>' : "",
                        tagline ? '<p class="cps-creator-tagline">' + esc(tagline) + '</p>' : "",
                        socialMarkup ? '<div class="cps-socials">' + socialMarkup + '</div>' : "",
                        '<div class="cps-hero__actions">',
                          heroPrimaryAction,
                          '<button class="cps-btn cps-btn--glass cps-btn--hero" type="button" data-share-store>Share</button>',
                        '</div>',
                        '<div class="cps-trust-strip" aria-label="Clikker store benefits">',
                          '<span>Secure payment</span>',
                          '<span>Direct from creator</span>',
                          '<span>Order tracking</span>',
                        '</div>',
                      '</div>',
                    '</div>',
                  '</div>',
                '</section>'
              ].join("")
            : "",


          '<nav class="cps-section-nav" aria-label="Store sections">',
            showProducts ? '<a href="#products">Offers</a>' : "",
            showAbout ? '<a href="#about">About</a>' : "",
            showTestimonials && testimonials.length ? '<a href="#reviews">Reviews</a>' : "",
            showFaq && faqs.length ? '<a href="#faq">FAQ</a>' : "",
            showContact && email ? '<a href="#contact">Contact</a>' : "",
          '</nav>',


          showProducts
            ? [
                '<section class="cps-section" id="products">',
                  '<div class="cps-section__head">',
                    '<div>',
                      '<span class="cps-eyebrow">' + (products.length === 1 ? 'Featured offer' : 'Offers from ' + esc(creatorName)) + '</span>',
                      '<h2>' + esc(
                        products.length === 1
                          ? featuredOfferHeading(creatorName, products[0])
                          : 'Choose how you want to connect'
                      ) + '</h2>',
                      '<p>' + esc(
                        products.length === 1
                          ? productFallbackDescription(products[0])
                          : 'Book a service, request something personal or access creator-made products directly.'
                      ) + '</p>',
                    '</div>',
                    types.length > 1
                      ? '<div class="cps-product-filter" aria-label="Filter products"><button class="is-active" type="button" data-product-filter="all" aria-pressed="true">All</button>' +
                        types.map(function (type) {
                          return '<button type="button" data-product-filter="' + esc(type) + '" aria-pressed="false">' + esc(typeLabel(type)) + '</button>';
                        }).join("") +
                        '</div>'
                      : "",
                  '</div>',
                  products.length
                    ? '<div class="cps-products" id="cpsProductsGrid">' + products.map(productCard).join("") + '</div>'
                    : '<div class="cps-empty"><h3>Offers coming soon</h3><p>This creator is preparing their first offer. Check back again soon.</p></div>',
                '</section>'
              ].join("")
            : "",

          showAbout
            ? [
                '<section class="cps-section" id="about" data-store-mode="' + esc(storeMode.mode) + '">',
                  '<div class="cps-about">',
                    '<div>',
                      '<span class="cps-eyebrow">About ' + esc(creatorName) + '</span>',
                      '<h2 class="cps-section-title">Meet ' + esc(creatorName) + '</h2>',
                      audience
                        ? '<p class="cps-audience-copy">Created for ' + esc(audience) + '.</p>'
                        : '<p class="cps-audience-copy">' + esc(categoryPresentation(category).label) + ' · Creator on Clikker</p>',
                    '</div>',
                    '<div class="cps-about__card"><p>' + esc(about) + '</p></div>',
                  '</div>',
                '</section>'
              ].join("")
            : "",

          showAchievements && achievements.length
            ? [
                '<section class="cps-section" id="achievements">',
                  '<span class="cps-eyebrow">Credibility</span>',
                  '<h2 class="cps-section-title" style="margin-bottom:24px">Achievements</h2>',
                  '<div class="cps-mini-grid">' + renderMiniCards(achievements, "title", "description") + '</div>',
                '</section>'
              ].join("")
            : "",

          showBrands && brands.length
            ? [
                '<section class="cps-section" id="brands">',
                  '<span class="cps-eyebrow">Collaborations</span>',
                  '<h2 class="cps-section-title" style="margin-bottom:24px">Brands and partners</h2>',
                  '<div class="cps-mini-grid">' + renderMiniCards(brands, "name", "description") + '</div>',
                '</section>'
              ].join("")
            : "",

          showTestimonials && testimonials.length
            ? [
                '<section class="cps-section" id="reviews">',
                  '<span class="cps-eyebrow">Reviews</span>',
                  '<h2 class="cps-section-title" style="margin-bottom:24px">What people say</h2>',
                  '<div class="cps-mini-grid">' + renderMiniCards(testimonials, "name", "quote") + '</div>',
                '</section>'
              ].join("")
            : "",

          showFaq && faqs.length
            ? [
                '<section class="cps-section" id="faq">',
                  '<span class="cps-eyebrow">Frequently asked questions</span>',
                  '<h2 class="cps-section-title" style="margin-bottom:24px">Before you order</h2>',
                  '<div class="cps-faq">',
                    faqs.map(function (item) {
                      var question = clean(firstValue(item.question, item.title, "Question"));
                      var answer = clean(firstValue(item.answer, item.description, ""));
                      return '<details><summary>' + esc(question) + '</summary><p>' + esc(answer) + '</p></details>';
                    }).join(""),
                  '</div>',
                '</section>'
              ].join("")
            : "",

          showContact && email
            ? [
                '<section class="cps-section" id="contact">',
                  '<div class="cps-contact">',
                    '<div>',
                      '<h3>Want to ask ' + esc(creatorName) + ' something first?</h3>',
                      '<p>Ask about availability, requirements or a custom collaboration before placing an order.</p>',
                    '</div>',
                    '<a class="cps-btn cps-btn--primary" href="mailto:' + esc(email) + '">Contact Creator</a>',
                  '</div>',
                '</section>'
              ].join("")
            : "",

          '<footer class="cps-footer">',
            '<span>© ' + new Date().getFullYear() + ' ' + esc(storeName) + '</span>',
            '<span>Powered by <a href="/">Clikker</a></span>',
          '</footer>',
        '</main>',


      '</div>'
    ].join("");

    var storefrontApp = root.querySelector(".cps");
    document.documentElement.setAttribute("data-clikker-portal-store", "true");
    document.body.classList.add("clikker-portal-store-active");

    if (storefrontApp) {
      storefrontApp.dataset.storeMode = storeMode.mode;
      storefrontApp.dataset.storeScore = String(storeMode.score);
      storefrontApp.style.setProperty("--cps-primary", branding.primary);
      storefrontApp.style.setProperty("--cps-secondary", branding.secondary);
      storefrontApp.style.setProperty("--cps-button", branding.button);
      storefrontApp.style.setProperty("--cps-text", branding.text);
    }

    document.querySelectorAll("[data-share-store]").forEach(function (button) {
      button.onclick = shareCurrentStore;
    });

    bindProductButtons();
    bindFilters();
    bindImageFallbacks(storeName);
    bindSectionNavigation();

    document.title = storeName + " | Clikker Creator Store";

    var description = document.querySelector('meta[name="description"]');
    if (!description) {
      description = document.createElement("meta");
      description.setAttribute("name", "description");
      document.head.appendChild(description);
    }

    description.setAttribute(
      "content",
      tagline || ("Explore products and services from " + creatorName + " on Clikker.")
    );

    console.info("[Clikker Public Store] Ready", VERSION, {
      mode: storeMode.mode,
      completenessScore: storeMode.score,
      meaningfulAbout: storeMode.meaningfulAbout,
      meaningfulTagline: storeMode.meaningfulTagline,
      socialCount: storeMode.socialCount,
      productCount: storeMode.productCount,
      hasBanner: storeMode.hasBanner,
      hasAvatar: storeMode.hasAvatar,
      headerMode: "portal"
    });

    try {
      window.dispatchEvent(new CustomEvent("clikker:store-rendered", {
        detail: {
          version: VERSION,
          storeId: clean(store && store.id),
          creatorId: clean(creator && creator.id),
          slug: clean(firstValue(store && store.slug, store && store.handle, slug)),
          preview: previewMode,
          productCount: products.length
        }
      }));
    } catch (_) {}
  }

  async function queryPublicProductsByField(field, storeId) {
    return withTimeout(
      window.Clikker.queryDocuments("products", {
        filters: [
          [field, "==", storeId],
          ["status", "==", "published"],
          ["visibility", "==", "public"]
        ],
        limit: 100
      }),
      QUERY_TIMEOUT_MS,
      "Products took too long to load."
    );
  }

  async function queryOwnerProducts(ownerId) {
    return withTimeout(
      window.Clikker.queryDocuments("products", {
        filters: [["ownerId", "==", ownerId]],
        limit: 100
      }),
      QUERY_TIMEOUT_MS,
      "Products took too long to load."
    );
  }

  async function loadProducts(storeId, isOwner) {
    var rows = [];
    var errors = [];
    var firebaseUser = window.Clikker.currentFirebaseUser || null;

    /*
      Firestore security rules are not post-query filters. A public query that
      requests every product for a store can include drafts/private documents,
      so Firestore rejects it even though JavaScript filters them afterwards.

      Public visitors must query only:
        storeId == current store
        status == published
        visibility == public
    */
    if (previewMode && isOwner && firebaseUser && firebaseUser.uid) {
      try {
        rows = await queryOwnerProducts(firebaseUser.uid);
        rows = rows.filter(function (product) {
          return clean(firstValue(product.storeId, product.store_id, "")) === clean(storeId);
        });
      } catch (ownerError) {
        errors.push(ownerError);
      }
    }

    if (!rows.length) {
      try {
        rows = await queryPublicProductsByField("storeId", storeId);
      } catch (firstError) {
        errors.push(firstError);

        try {
          rows = await queryPublicProductsByField("store_id", storeId);
        } catch (secondError) {
          errors.push(secondError);
        }
      }
    }

    if (!rows.length && errors.length) {
      console.warn("[Clikker Public Store] Product queries failed", errors);
    }

    rows.sort(function (a, b) {
      var orderA = Number(firstValue(a.sortOrder, a.sort_order, 0));
      var orderB = Number(firstValue(b.sortOrder, b.sort_order, 0));

      if (orderA !== orderB) return orderA - orderB;

      var dateA = window.Clikker.parseDate(firstValue(a.createdAt, a.created_at, null));
      var dateB = window.Clikker.parseDate(firstValue(b.createdAt, b.created_at, null));

      return (dateB ? dateB.getTime() : 0) - (dateA ? dateA.getTime() : 0);
    });

    return previewMode && isOwner
      ? rows.filter(function (product) {
          var status = clean(product.status).toLowerCase();
          return status !== "deleted" && status !== "archived";
        })
      : rows.filter(function (product) {
          var status = clean(product.status).toLowerCase();
          var visibility = clean(product.visibility || "public").toLowerCase();
          return status === "published" && visibility === "public";
        });
  }

  async function boot() {
    if (!slug) {
      activateStoreGateway();
      return;
    }

    try {
      await waitForCore();

      if (
        !window.Clikker ||
        !window.Clikker.db ||
        typeof window.Clikker.queryDocuments !== "function"
      ) {
        throw new Error("A compatible Clikker Firebase core is required.");
      }

      if (
        ProductEngine &&
        typeof ProductEngine.getWorkflowMeta !== "function"
      ) {
        throw new Error("A compatible Clikker Product Engine is required.");
      }

      var normalizedSlug = normalizeSlug(slug);
      var stores = [];
      var rawStore = null;

      /*
        Public Firestore rules commonly allow direct reads but deny collection
        queries. The creator onboarding already reserves:
          storeHandles/{handle} -> { storeId, ownerId, creatorId }

        Resolve the handle document first, then read stores/{storeId} directly.
        This avoids the denied slug/handle list queries shown in the console.
      */
      try {
        var handleSnap = await withTimeout(
          db().collection("storeHandles").doc(normalizedSlug).get(),
          QUERY_TIMEOUT_MS,
          "The creator link took too long to resolve."
        );

        if (handleSnap.exists) {
          var handleData = handleSnap.data() || {};
          var resolvedStoreId = clean(
            firstValue(handleData.storeId, handleData.store_id, "")
          );

          if (resolvedStoreId) {
            var storeSnap = await withTimeout(
              db().collection("stores").doc(resolvedStoreId).get(),
              QUERY_TIMEOUT_MS,
              "The store took too long to load."
            );

            if (storeSnap.exists) {
              rawStore = Object.assign(
                { id: storeSnap.id },
                storeSnap.data() || {}
              );
            }
          }
        }
      } catch (directLookupError) {
        console.warn(
          "[Clikker Public Store] Direct handle lookup failed",
          directLookupError
        );
      }

      /*
        Compatibility fallback for older stores that do not yet have a
        storeHandles document. These queries may be denied by stricter rules,
        so failure is non-fatal.
      */
      if (!rawStore) {
        try {
          stores = await withTimeout(
            window.Clikker.queryDocuments("stores", {
              filters: [["slug", "==", normalizedSlug]],
              limit: 1
            }),
            QUERY_TIMEOUT_MS,
            "The store took too long to load."
          );
        } catch (slugError) {
          console.warn("[Clikker Public Store] Slug query fallback failed", slugError);
        }

        if (!stores.length) {
          try {
            stores = await withTimeout(
              window.Clikker.queryDocuments("stores", {
                filters: [["handle", "==", normalizedSlug]],
                limit: 1
              }),
              QUERY_TIMEOUT_MS,
              "The store took too long to load."
            );
          } catch (handleError) {
            console.warn("[Clikker Public Store] Handle query fallback failed", handleError);
          }
        }

        if (stores.length) {
          rawStore = stores[0];
        }
      }

      if (!rawStore) {
        errorView(
          "Store not found",
          "This creator store does not exist, is unavailable, or its public handle is not reserved."
        );
        return;
      }

      var store = window.Clikker.normalizeStore(rawStore, rawStore.id);

      var firebaseUser = window.Clikker.currentFirebaseUser || null;
      var isOwner = !!(
        window.Clikker.isAuthenticated() &&
        firebaseUser &&
        (
          rawStore.ownerId === firebaseUser.uid ||
          rawStore.owner_id === firebaseUser.uid ||
          store.ownerId === firebaseUser.uid
        )
      );

      if (previewMode && !isOwner) {
        errorView("Preview unavailable", "Only the store owner can open a private preview.");
        return;
      }

      if (!previewMode) {
        var status = clean(firstValue(store.status, rawStore.status, ""));
        var visibility = clean(firstValue(store.visibility, rawStore.visibility, ""));

        var published = status === "published" || status === "active";
        var publicVisibility = !visibility || visibility === "public";

        if (!published || !publicVisibility) {
          errorView("Store unavailable", "This creator store is not currently published.");
          return;
        }
      }

      var creator = null;
      var creatorId = clean(firstValue(
        store.creatorId,
        rawStore.creatorId,
        rawStore.creator_id,
        ""
      ));

      var storeHasPublicIdentity = !!clean(firstValue(
        rawStore.storeName,
        rawStore.displayName,
        store.storeName,
        store.displayName,
        ""
      ));

      var storeHasPublicProfileImage = !!clean(firstValue(
        rawStore.profileImage,
        rawStore.profileImageURL,
        rawStore.profileImageUrl,
        nested(rawStore, "raw.profileImage", ""),
        nested(rawStore, "raw.profileImageURL", ""),
        store.profileImage,
        store.profileImageURL,
        store.profileImageUrl,
        ""
      ));

      /*
        Public creator documents may be protected by Firestore rules.
        A published store already contains the public identity needed to render.
        Only request creators/{creatorId} as a compatibility fallback when the
        store itself does not carry enough identity data.
      */
      if (creatorId && (!storeHasPublicIdentity || !storeHasPublicProfileImage)) {
        try {
          var creatorDoc = await withTimeout(
            window.Clikker.getDocument("creators", creatorId),
            QUERY_TIMEOUT_MS,
            "The creator profile took too long to load."
          );

          if (creatorDoc) {
            creator = window.Clikker.normalizeCreator(creatorDoc, creatorId);
          }
        } catch (creatorError) {
          console.info("[Clikker Public Store] Creator profile fallback unavailable.");
        }
      }

      var products = await loadProducts(store.id, isOwner);
      render(store, creator, products);
    } catch (error) {
      console.error("[Clikker Public Store]", error);

      var message = error && error.message
        ? error.message
        : "The creator store could not be loaded. Check the link and try again.";

      if (/permission|insufficient/i.test(message)) {
        message = "This store could not be accessed because its public permissions are not configured correctly.";
      }

      errorView("Unable to load store", message);
    }
  }


  function publishStoreAPI() {
    StoreAPI.version = VERSION;
    StoreAPI.page = "public-store";
    StoreAPI.events = Object.assign({}, StoreAPI.events || {}, {
      rendered: "clikker:store-rendered"
    });

    StoreAPI.getStore = function () {
      return currentStore;
    };

    StoreAPI.getCreator = function () {
      return currentCreator;
    };

    StoreAPI.getProducts = function () {
      return allProducts.slice();
    };

    StoreAPI.getProductById = function (productId) {
      var id = clean(productId);
      if (!id) return null;

      return allProducts.find(function (product) {
        return clean(product && product.id) === id;
      }) || null;
    };

    StoreAPI.getSlug = function () {
      return slug;
    };

    StoreAPI.isPreview = function () {
      return previewMode;
    };

    StoreAPI.getState = function () {
      return {
        version: VERSION,
        slug: slug,
        previewMode: previewMode,
        store: currentStore,
        creator: currentCreator,
        products: allProducts.slice(),
        currentFilter: currentFilter
      };
    };

    StoreAPI.openProduct = openProductFromCheckout;
    StoreAPI.bindProductButtons = bindProductButtons;
    StoreAPI.showToast = showToast;

    StoreAPI.utils = Object.assign({}, StoreAPI.utils || {}, {
      clean: clean,
      validColor: validColor,
      normalizeExternalURL: normalizeExternalURL,
      normalizeSlug: normalizeSlug,
      toArray: toArray,
      firstValue: firstValue,
      nested: nested,
      boolValue: boolValue,
      withTimeout: withTimeout,
      db: db,
      serverTimestamp: serverTimestamp,
      waitForCore: waitForCore,
      esc: esc,
      safeUrl: safeUrl,
      safeImageUrl: safeImageUrl,
      money: money,
      normalizedProductType: normalizedProductType,
      workflowMeta: workflowMeta,
      workflowDefinition: workflowDefinition,
      typeOf: typeOf,
      typeLabel: typeLabel,
      productCTA: productCTA,
      productIcon: productIcon,
      rawOf: rawOf,
      fulfilmentOf: fulfilmentOf,
      toSnakeCase: toSnakeCase,
      toCamelCase: toCamelCase,
      productValue: productValue,
      humanizeValue: humanizeValue,
      deliveryMeta: deliveryMeta
    });
  }

  publishStoreAPI();
  boot();

  StoreAPI.defaultCreatorBanner = "https://cdn.shopify.com/s/files/1/0928/6136/6587/files/Creator_Banner.jpg?v=1786798200";
  StoreAPI.getDefaultCreatorBanner = function () {
    return defaultCreatorBanner();
  };

})();
